import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findValueInEnum, maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import { PaymentChannelCodeEnum } from "@saleor/types/globalTypes";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  paymentEnum,
  StoreOrderBoolean,
  StoreOrdersFinanceFilterKeys,
  StoreOrdersFinanceListFilterOpts
} from "../../components/StoreOrdersFinanceListPage/filters";
import {
  InitialStoreOrdersFinanceFilterData_brands_edges_node,
  InitialStoreOrdersFinanceFilterData_malls_edges_node
} from "../../types/InitialStoreOrdersFinanceFilterData";
import {
  storeOrdersFinanceListUrlFilters,
  StoreOrdersFinanceListUrlFiltersEnum,
  StoreOrdersFinanceListUrlFiltersWithMultipleValues,
  StoreOrdersFinanceUrlQueryParams
} from "../../urls";
export const SALESREPORTS_FILTERS_KEY = "StoreOrdersFinanceFilters";

export function getFilterOpts(
  params: storeOrdersFinanceListUrlFilters,
  brands: {
    initial: InitialStoreOrdersFinanceFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialStoreOrdersFinanceFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): StoreOrdersFinanceListFilterOpts {
  const paymentMethod = params.paymentMethodType?.split(" ");
  const paymentMethodType = paymentMethod ? paymentMethod[0] : null;
  const channelCode = paymentMethod ? paymentMethod[1] : null;
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    canceledAt: {
      active: maybe(
        () =>
          [params.orderCanceledFrom, params.orderCanceledTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCanceledTo, ""),
        min: maybe(() => params.orderCanceledFrom, "")
      }
    },
    channelCode: {
      active: !!channelCode,
      value: maybe(() =>
        channelCode
          ? findValueInEnum(channelCode, PaymentChannelCodeEnum)
          : null
      )
    },
    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    isPayoutEligible: {
      active: !!params.isPayoutEligible,
      value: maybe(() => params.isPayoutEligible, "")
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderDate: {
      active: maybe(
        () =>
          [params.orderDateFrom, params.orderDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderDateTo, ""),
        min: maybe(() => params.orderDateFrom, "")
      }
    },
    orderStatus: {
      active: !!params.orderStatus,
      value: maybe(() => dedupeFilter(params.orderStatus), null)
    },
    paymentMethodType: {
      active: !!paymentMethodType,
      value: maybe(() => paymentMethodType, "")
    },
    shippingMethodName: {
      active: !!params.deliveryType,
      value: maybe(() => dedupeFilter(params.deliveryType), null)
    }
  };
}

export function getFilterVariables(params: storeOrdersFinanceListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();
  const paymentMethod = params.paymentMethodType?.split(" ");
  const paymentMethodType = paymentMethod ? paymentMethod[0] : null;
  const channelCode = paymentMethod ? paymentMethod[1] : null;

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );
  const endDate = moment(`${endYear}-${endMonth}-${endDay}`)
    .add(1, "days")
    .format("YYYY-MM-DD");

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    canceledAt: getGteLteVariables({
      gte: params.orderCanceledFrom
        ? moment(`${params.orderCanceledFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCanceledTo
        ? moment(`${params.orderCanceledTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    channelCode: maybe(() =>
      channelCode ? findValueInEnum(channelCode, PaymentChannelCodeEnum) : null
    ),
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    isPayoutEligible: params?.isPayoutEligible
      ? params.isPayoutEligible === "true"
      : null,
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),

    orderDate: getGteLteVariables({
      gte: params.orderDateFrom
        ? moment(`${params.orderDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.orderDateFrom &&
          (params.orderCompleteDateFrom || params.orderCanceledFrom)
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),

      lte: params.orderDateTo
        ? moment(`${params.orderDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.orderDateTo &&
          (params.orderCompleteDateTo || params.orderCanceledTo)
        ? null
        : endDate
    }),
    orderStatus: maybe(() => params.orderStatus, null),
    paymentMethodType,
    search: params.query,
    shippingMethodName: maybe(() => params.deliveryType, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<StoreOrdersFinanceFilterKeys>,
  params: storeOrdersFinanceListUrlFilters
): storeOrdersFinanceListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case StoreOrdersFinanceFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersWithMultipleValues.mall
      );
    case StoreOrdersFinanceFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersWithMultipleValues.brand
      );

    case StoreOrdersFinanceFilterKeys.orderDate:
      return getMinMaxQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersEnum.orderDateFrom,
        StoreOrdersFinanceListUrlFiltersEnum.orderDateTo
      );
    case StoreOrdersFinanceFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersEnum.orderCompleteDateFrom,
        StoreOrdersFinanceListUrlFiltersEnum.orderCompleteDateTo
      );
    case StoreOrdersFinanceFilterKeys.isPayoutEligible:
      return getSingleEnumValueQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersEnum.isPayoutEligible,
        StoreOrderBoolean
      );
    case StoreOrdersFinanceFilterKeys.orderStatus:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersWithMultipleValues.orderStatus
      );
    case StoreOrdersFinanceFilterKeys.canceledAt:
      return getMinMaxQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersEnum.orderCanceledFrom,
        StoreOrdersFinanceListUrlFiltersEnum.orderCanceledTo
      );
    case StoreOrdersFinanceFilterKeys.paymentMethodType:
      return getSingleEnumValueQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersEnum.paymentMethodType,
        paymentEnum
      );
    case StoreOrdersFinanceFilterKeys.shippingMethodName:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersFinanceListUrlFiltersWithMultipleValues.deliveryType
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<storeOrdersFinanceListUrlFilters>(
  SALESREPORTS_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  StoreOrdersFinanceUrlQueryParams,
  storeOrdersFinanceListUrlFilters
>({
  ...StoreOrdersFinanceListUrlFiltersEnum,
  ...StoreOrdersFinanceListUrlFiltersWithMultipleValues
});
